import { useEffect, useRef } from 'react';
import { useEditor, useValue } from 'tldraw';
import { useBoardStore } from '../store/useBoardStore';
import { INTENT_EVENT_TYPES, pushBoardIntentEvent, truncateIntentLabel } from '../utils/boardRecentIntent';
import { getActivityId } from '../utils/autoLayout';
import { getActivityTitle } from '../utils/boardSelectionContext';

function firstSelectedPinActivityId(editor) {
  if (!editor?.getSelectedShapeIds) return '';
  for (const shapeId of editor.getSelectedShapeIds() || []) {
    const shape = editor.getShape(shapeId);
    if (shape?.type === 'pin-card' && shape.meta?.activityId) {
      return String(shape.meta.activityId);
    }
  }
  return '';
}

/**
 * Keep focusedPinId in sync with pin selection.
 * Select a pin → focus (modify). Deselect with the panel closed → clear (delete).
 */
export default function CanvasFocusTracker() {
  const editor = useEditor();
  const hashtag = useBoardStore(s => s.hashtag);
  const setFocusedPinId = useBoardStore(s => s.setFocusedPinId);
  const selectedActivityId = useBoardStore(s => s.selectedActivityId);
  const selectedPinId = useValue(
    'canvas-focus-selected-pin',
    () => firstSelectedPinActivityId(editor),
    [editor]
  );
  const prevFocusRef = useRef(null);

  useEffect(() => {
    const nextId = selectedPinId || (selectedActivityId ? String(selectedActivityId) : '');
    const prev = prevFocusRef.current;
    if (prev === (nextId || null)) return;

    const store = useBoardStore.getState();
    if (nextId) {
      const activity = (store.activities || []).find(a => getActivityId(a) === nextId);
      const label = truncateIntentLabel(getActivityTitle(activity) || nextId.slice(-6));
      if (hashtag) {
        pushBoardIntentEvent(hashtag, {
          type: INTENT_EVENT_TYPES.SELECTION_FOCUS,
          label,
        });
      }
      setFocusedPinId(nextId);
      prevFocusRef.current = nextId;
      return;
    }

    if (prev && hashtag) {
      const activity = (store.activities || []).find(a => getActivityId(a) === prev);
      pushBoardIntentEvent(hashtag, {
        type: INTENT_EVENT_TYPES.FOCUS_CLEARED,
        label: truncateIntentLabel(getActivityTitle(activity) || prev.slice(-6)),
      });
    }
    setFocusedPinId(null);
    prevFocusRef.current = null;
  }, [hashtag, selectedPinId, selectedActivityId, setFocusedPinId]);

  return null;
}
